import React, { useState, useMemo } from 'react';
import { Card, CardContent, Typography, Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Select, MenuItem, FormControl, InputLabel, Alert } from '@mui/material';
import { ROUTE_PERMISSIONS, getRoutesByCategory, RoutePermissionConfig } from '../../config/routePermissions';
import { Permission, UserRole, ROLE_PERMISSIONS } from '../../utils/rolePermissions';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Admin component for viewing and understanding route permissions
 * This is a read-only view to help administrators understand the permission system
 */
export const RoutePermissionManager: React.FC = () => {
    const { isAdmin } = usePermissions();
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [selectedRole, setSelectedRole] = useState<string>('all');

    const categories = useMemo(() => getRoutesByCategory(), []);
    
    const filteredRoutes = useMemo(() => {
        let routes = selectedCategory === 'all' ? ROUTE_PERMISSIONS : categories[selectedCategory as keyof typeof categories] || [];
        
        if (searchTerm) {
            routes = routes.filter(route => 
                route.path.toLowerCase().includes(searchTerm.toLowerCase()) ||
                route.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        if (selectedRole !== 'all') {
            const rolePermissions = ROLE_PERMISSIONS[selectedRole as UserRole] || [];
            routes = routes.filter(route => {
                if (!route.requiredPermissions || route.requiredPermissions.length === 0) return true;
                
                if (route.requireAny) {
                    return route.requiredPermissions.some(perm => rolePermissions.includes(perm));
                } else {
                    return route.requiredPermissions.every(perm => rolePermissions.includes(perm));
                }
            });
        }

        return routes;
    }, [selectedCategory, searchTerm, selectedRole, categories]);

    if (!isAdmin()) {
        return (
            <Alert severity="error">
                You need administrator privileges to access the Route Permission Manager.
            </Alert>
        );
    }

    const getPermissionColor = (permission: Permission): 'primary' | 'secondary' | 'success' | 'warning' | 'error' => {
        if (permission.includes('VIEW')) return 'primary';
        if (permission.includes('CREATE') || permission.includes('EDIT')) return 'success';
        if (permission.includes('MANAGE') || permission.includes('ADMIN')) return 'error';
        if (permission.includes('PERFORM')) return 'warning';
        return 'secondary';
    };

    const getRoleAccessStatus = (route: RoutePermissionConfig, role: UserRole): 'full' | 'partial' | 'none' => {
        if (!route.requiredPermissions || route.requiredPermissions.length === 0) return 'full';
        
        const rolePermissions = ROLE_PERMISSIONS[role] || [];
        
        if (route.requireAny) {
            return route.requiredPermissions.some(perm => rolePermissions.includes(perm)) ? 'full' : 'none';
        } else {
            const hasAll = route.requiredPermissions.every(perm => rolePermissions.includes(perm));
            const hasSome = route.requiredPermissions.some(perm => rolePermissions.includes(perm));
            
            if (hasAll) return 'full';
            if (hasSome) return 'partial';
            return 'none';
        }
    };

    const getAccessStatusColor = (status: 'full' | 'partial' | 'none'): string => {
        switch (status) {
            case 'full': return '#4caf50';
            case 'partial': return '#ff9800';
            case 'none': return '#f44336';
        }
    };

    const getAccessStatusText = (status: 'full' | 'partial' | 'none'): string => {
        switch (status) {
            case 'full': return 'Full Access';
            case 'partial': return 'Partial Access';
            case 'none': return 'No Access';
        }
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Route Permission Manager
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                View and understand how routes are protected by role-based permissions. This system automatically 
                controls access to pages based on user roles and permissions.
            </Typography>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    label="Search routes"
                    variant="outlined"
                    size="small"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: 200 }}
                />
                
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Category</InputLabel>
                    <Select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        label="Category"
                    >
                        <MenuItem value="all">All Routes</MenuItem>
                        <MenuItem value="dashboard">Dashboard</MenuItem>
                        <MenuItem value="applications">Applications</MenuItem>
                        <MenuItem value="loanProcessing">Loan Processing</MenuItem>
                        <MenuItem value="collections">Collections</MenuItem>
                        <MenuItem value="loanBook">Loan Book</MenuItem>
                        <MenuItem value="customers">Customers</MenuItem>
                        <MenuItem value="accounts">Accounts</MenuItem>
                        <MenuItem value="documents">Documents</MenuItem>
                        <MenuItem value="reports">Reports</MenuItem>
                        <MenuItem value="admin">Administration</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter by Role</InputLabel>
                    <Select
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        label="Filter by Role"
                    >
                        <MenuItem value="all">All Roles</MenuItem>
                        {Object.values(UserRole).map(role => (
                            <MenuItem key={role} value={role}>{role}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Statistics */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Card sx={{ minWidth: 200 }}>
                    <CardContent>
                        <Typography variant="h6">{filteredRoutes.length}</Typography>
                        <Typography variant="body2" color="text.secondary">Total Routes</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 200 }}>
                    <CardContent>
                        <Typography variant="h6">
                            {filteredRoutes.filter(r => r.requiredPermissions && r.requiredPermissions.length > 0).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Protected Routes</Typography>
                    </CardContent>
                </Card>
                <Card sx={{ minWidth: 200 }}>
                    <CardContent>
                        <Typography variant="h6">
                            {filteredRoutes.filter(r => !r.requiredPermissions || r.requiredPermissions.length === 0).length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Open Routes</Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Routes Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Route Path</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell>Required Permissions</TableCell>
                            <TableCell>Permission Logic</TableCell>
                            {Object.values(UserRole).map(role => (
                                <TableCell key={role} align="center">{role}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRoutes.map((route, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                        {route.path}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {route.description || 'No description'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {route.requiredPermissions && route.requiredPermissions.length > 0 ? (
                                            route.requiredPermissions.map(permission => (
                                                <Chip
                                                    key={permission}
                                                    label={permission}
                                                    size="small"
                                                    color={getPermissionColor(permission)}
                                                    variant="outlined"
                                                />
                                            ))
                                        ) : (
                                            <Chip label="No permissions required" size="small" color="success" />
                                        )}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    {route.requiredPermissions && route.requiredPermissions.length > 0 ? (
                                        <Chip
                                            label={route.requireAny ? 'ANY' : 'ALL'}
                                            size="small"
                                            color={route.requireAny ? 'warning' : 'primary'}
                                        />
                                    ) : (
                                        <Chip label="OPEN" size="small" color="success" />
                                    )}
                                </TableCell>
                                {Object.values(UserRole).map(role => {
                                    const status = getRoleAccessStatus(route, role);
                                    return (
                                        <TableCell key={role} align="center">
                                            <Chip
                                                label={getAccessStatusText(status)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: getAccessStatusColor(status),
                                                    color: 'white',
                                                    fontSize: '0.75rem'
                                                }}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredRoutes.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        No routes found matching your filters.
                    </Typography>
                </Box>
            )}

            {/* Legend */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="h6" gutterBottom>Legend</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="subtitle2">Permission Logic:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip label="ALL" size="small" color="primary" />
                            <Typography variant="body2">User needs ALL listed permissions</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip label="ANY" size="small" color="warning" />
                            <Typography variant="body2">User needs ANY of the listed permissions</Typography>
                        </Box>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2">Access Status:</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip label="Full Access" size="small" sx={{ backgroundColor: '#4caf50', color: 'white' }} />
                            <Typography variant="body2">Role has all required permissions</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip label="No Access" size="small" sx={{ backgroundColor: '#f44336', color: 'white' }} />
                            <Typography variant="body2">Role lacks required permissions</Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
